import {DashboardEntity} from '../interfaces/Dashboard';
import {Permission} from './acl';

export const PANEL_CODES = [
  'HazardAI',
  'HazardAIDectionLog',
  'HazardAIMachineUtilizationChart',
  'HazardAIMachineSpeedChart',
  'HazardAISummaryChart',
  'HazardAITotalDetections',
  'HazardAITotalHazardEvents',
  'HazardAITotalWarningEvents',
  'HazardAIHighSpeedEvent',
  'HazardAIActiveVehicles',
  'HazardAIMachineInteractions',
  'HazardAIMachineLocationHistory',
  'HazardAIDailyBreakdown',
  'HazardHeatMapReports',
  'Map',
  'AlarmHistoryReports',
  'AlarmLogReports',
  'AlarmGrid',
  'CnWifiNodes',
  'EmployeeAssetsGrid',
  'MachineDetailsReport',
  'MachineOverviewReport',
  'MachineHistoryReport',
  'MachineStatusReport',
  'EmployeeHistoryReport',
  'EmployeeOverviewReport',
  'MachineInteractionReport',
  'MachineSummaryReport',
  'CheckoutStationOverviewReport',
  'CheckoutStationDetailsReport',
  'InteractionsPerMachine',
  'TopEmployeeInteractions',
  'ConnectView',
  'MCronLogs',
  'MProcessorLogs',
  'SystemServicesTable',
  'CommtracNodeTrackingReports',
  'NodeTrackingReports',
  'NetworkDiagnostics',
  'BackHaulers',
  'SentroGateway',
  'InteractionsLastDays',
  'Nginx',
  'Backend',
  'Mqtt',
  'GasMonitoringReport',
  'AmsEmoduleInstallationHistory',
  'AmsEmoduleSensorHistory',
  'AMSEModuleCalibration',
  'BeltHistoryReport',
  'BeltLocationHistoryReport',
  'ShaftClearance',
  'ShaftClearanceReport',
  'AMSShortTermHistoryReport',
  'AMSLongTermHistoryReport',
  'AMSLocationHistoryReport',
  'MachineDetectionsByCamera',
  'TimeOfDayDetections',
] as const;

export type PanelCode = (typeof PANEL_CODES)[number];

export const PANEL_PRODUCT_CODES = [
  'core',
  'connect',
  'wifi',
  'belts',
  'proximity',
  'ams',
  'shaft_clearance',
  'ventilation',
  'hazard',
  'alarm',
] as const;

export type PanelProductCode = (typeof PANEL_PRODUCT_CODES)[number];

export type PanelDateRangeCapability =
  /** Panel accepts date_start + date_end filter params (most HAI panels). */
  | 'range'
  /** Panel accepts a single date param; it will receive the dashboard range's end date. */
  | 'single'
  /** Panel does not participate in the dashboard-wide date range (default when omitted). */
  | 'none';

export const PANELS_DATA: {
  [key in PanelCode]: {
    products: PanelProductCode[];
    permissions?: Permission[];
    roles?: number[];
    accessChecker?: () => boolean;
    /**
     * Whether this panel type participates in the dashboard-wide date range.
     * Omitting this field is equivalent to 'none'.
     */
    dateRange?: PanelDateRangeCapability;
    /**
     * When present, this panel uses the KPI layout variant: a full-bleed panel
     * body with a thick coloured left border and no header title.
     * `accent` is a MUI theme token (e.g. 'error.main').
     */
    kpi?: {accent: string};
  };
} = {
  MachineDetailsReport: {
    products: ['proximity'],
    dateRange: 'range',
  },
  MachineOverviewReport: {
    products: ['proximity'],
    dateRange: 'range',
  },
  MachineHistoryReport: {
    products: ['proximity'],
    dateRange: 'range',
  },
  MachineStatusReport: {
    products: ['proximity'],
  },
  EmployeeHistoryReport: {
    products: ['proximity'],
    dateRange: 'range',
  },
  EmployeeOverviewReport: {
    products: ['proximity'],
    dateRange: 'range',
  },
  MachineInteractionReport: {
    products: ['proximity'],
    dateRange: 'range',
  },
  MachineSummaryReport: {
    products: ['proximity'],
    dateRange: 'range',
  },
  CheckoutStationOverviewReport: {
    products: ['proximity'],
    dateRange: 'range',
  },
  CheckoutStationDetailsReport: {
    products: ['proximity'],
    dateRange: 'range',
  },
  InteractionsPerMachine: {
    products: ['proximity'],
    dateRange: 'range',
  },
  TopEmployeeInteractions: {
    products: ['proximity'],
    dateRange: 'range',
  },
  ConnectView: {
    products: ['connect'],
  },
  HazardAI: {
    products: ['hazard'],
    dateRange: 'range',
  },
  HazardAIDectionLog: {
    products: ['hazard'],
    dateRange: 'range',
  },
  HazardAIMachineUtilizationChart: {
    products: ['hazard'],
    dateRange: 'range',
  },
  HazardAIMachineSpeedChart: {
    products: ['hazard'],
    dateRange: 'range',
  },
  HazardAISummaryChart: {
    products: ['hazard'],
    dateRange: 'range',
  },
  HazardAITotalDetections: {
    products: ['hazard'],
    dateRange: 'range',
    kpi: {accent: 'error.main'},
  },
  HazardAITotalHazardEvents: {
    products: ['hazard'],
    dateRange: 'range',
    kpi: {accent: 'error.main'},
  },
  HazardAITotalWarningEvents: {
    products: ['hazard'],
    dateRange: 'range',
    kpi: {accent: 'error.main'},
  },
  HazardAIHighSpeedEvent: {
    products: ['hazard'],
    dateRange: 'range',
    kpi: {accent: 'warning.main'},
  },
  HazardAIActiveVehicles: {
    products: ['hazard'],
    kpi: {accent: 'info.main'},
  },
  HazardAIMachineInteractions: {
    products: ['hazard'],
    dateRange: 'range',
  },
  HazardHeatMapReports: {
    products: ['hazard'],
    permissions: ['get::/hazard-ai/detection-heat-map'],
    dateRange: 'range',
  },
  HazardAIMachineLocationHistory: {
    products: ['hazard'],
    dateRange: 'range',
  },
  HazardAIDailyBreakdown: {
    products: ['hazard'],
    dateRange: 'range',
  },
  Map: {
    products: ['connect'],
  },
  InteractionsLastDays: {
    products: ['proximity'],
    dateRange: 'range',
  },
  MCronLogs: {
    products: ['core'],
    permissions: ['get::/m-cron/status'],
  },
  MProcessorLogs: {
    products: ['core'],
    permissions: ['get::/m-processor/get-logs'],
  },
  SystemServicesTable: {
    products: ['core'],
    permissions: ['get::/system/status'],
  },
  CommtracNodeTrackingReports: {
    products: ['connect'],
    dateRange: 'range',
  },
  NodeTrackingReports: {
    products: ['connect'],
  },
  NetworkDiagnostics: {
    products: ['connect'],
  },
  BackHaulers: {
    products: ['core'],
    permissions: ['get::/backhauler'],
  },
  SentroGateway: {
    products: ['ams'],
  },
  AlarmHistoryReports: {
    products: ['alarm'],
    dateRange: 'range',
  },
  AlarmLogReports: {
    products: ['alarm'],
    dateRange: 'range',
  },
  AlarmGrid: {
    products: ['alarm'],
  },
  CnWifiNodes: {
    products: ['connect'],
  },
  EmployeeAssetsGrid: {
    products: ['connect'],
  },
  Nginx: {
    products: ['core'],
    permissions: ['get::/backend/frontend/summary'],
  },
  Backend: {
    products: ['core'],
    permissions: ['get::/backend/summary'],
  },
  Mqtt: {
    products: ['core'],
    permissions: ['get::/backend/mqtt/summary'],
  },
  GasMonitoringReport: {
    products: ['ams'],
  },
  AmsEmoduleInstallationHistory: {
    products: ['ams'],
    dateRange: 'range',
  },
  AmsEmoduleSensorHistory: {
    products: ['ams'],
    dateRange: 'range',
  },
  AMSEModuleCalibration: {
    products: ['ams'],
    dateRange: 'range',
  },
  BeltHistoryReport: {
    products: ['belts'],
    dateRange: 'range',
  },
  BeltLocationHistoryReport: {
    products: ['belts'],
    dateRange: 'range',
  },
  ShaftClearance: {
    products: ['shaft_clearance'],
  },
  ShaftClearanceReport: {
    products: ['shaft_clearance'],
  },
  AMSShortTermHistoryReport: {
    products: ['ams'],
  },
  AMSLongTermHistoryReport: {
    products: ['ams'],
  },
  AMSLocationHistoryReport: {
    products: ['ams'],
  },
  MachineDetectionsByCamera: {
    products: ['hazard'],
    dateRange: 'range',
  },
  TimeOfDayDetections: {
    products: ['hazard'],
    dateRange: 'range',
  },
};

// ---------------------------------------------------------------------------
// Dashboard-date-range helpers
// ---------------------------------------------------------------------------

/**
 * Returns true when the given panel type participates in the dashboard-wide
 * date range (i.e. its dateRange capability is 'range' or 'single').
 */
export const panelUsesDashboardDate = (code: PanelCode): boolean => {
  const cap = PANELS_DATA[code]?.dateRange;
  return cap === 'range' || cap === 'single';
};

/**
 * Returns true when every panel entity on the dashboard belongs solely to the
 * HazardAI product family.
 * A dashboard with zero panel entities returns false.
 */
export const isHaiOnlyDashboard = (entities: DashboardEntity[]): boolean => {
  const panelEntities = entities.filter(
    (e) => e.type === 'panel' && e.panel?.code
  );
  if (panelEntities.length === 0) {
    return false;
  }
  return panelEntities.every((e) =>
    PANELS_DATA[e.panel!.code!]?.products.includes('hazard')
  );
};

/**
 * Returns true when at least one panel entity on the dashboard participates in
 * the dashboard-wide date range (i.e. its dateRange capability is 'range' or
 * 'single'). Used to gate the dashboard date-range control so it only shows
 * when there is something to drive.
 */
export const dashboardHasDatePanels = (entities: DashboardEntity[]): boolean =>
  entities.some(
    (e) =>
      e.type === 'panel' &&
      !!e.panel?.code &&
      panelUsesDashboardDate(e.panel.code)
  );

/**
 * Returns the KPI layout config for the given panel code, or undefined when
 * the panel is not a KPI panel. The returned `accent` is a MUI theme token
 * (e.g. 'error.main') for the panel's left-border colour.
 */
export const getKpiPanelConfig = (
  code?: string
): {accent: string} | undefined => {
  if (!code) {
    return undefined;
  }
  return PANELS_DATA[code as PanelCode]?.kpi;
};
